import * as LocalizationHelper from '@/Utility/LocalizationHelper';
import ImageHandler from '@/Utility/ImageHandler';
import StorageHelper from '@/Utility/StorageHelper';
import { IRADEnvironmentProxy } from '@/Communication/RADEnvironmentProxy';
import * as CoreFrameDTOs from "coreframetypings/dto";
import * as Interfaces from '#/CoreFrameInterfaces';
import Utilities from '@/Utility/Utilities';
import { IRADApplication } from '@/Management/RADApplication';
import CurrencyHelper from '@/Utility/CurrencyHelper';
import { inject, singleton } from "tsyringe";
import { IResourceManager } from "@/Management/ResourceManager";

declare let radMessages;

/**
 * Repräsentiert eine Einheit zur Verwaltung der Sprachvariaten und zum Wechsel der Sprache
 */
@singleton()
export class ResourceManagerImpl implements IResourceManager {

    /**
     * Ressource-Elemente der aktuellen Sprachvariante
     */
    resources: KnockoutObservable<{}> = ko.observable({});

    /**
     * Liste aller aktiven Sprachen für die Anwendung
     */
    language: { data?: KnockoutObservableArray<any>, value?: KnockoutComputed<string> } = {
        data: ko.observableArray(),
        value: StorageHelper.bindGlobal('lastLoggedInLanguage', null)
    };

    currentLoadedLang: string = '';
    readonly MLSTRING_SEPARATOR: string = '!#/sp[$)';
    
    constructor(
        @inject("RADApplication") private app: IRADApplication,
        @inject("RADEnvironmentProxy") private proxy: IRADEnvironmentProxy,
    ) {
    }


    /**
     * Liefert das DTO der aktuell ausgewählten Sprache
     * oder des übergebenen ISO-Codes
     * @returns {LanguageDTO|null} Das Language-DTO der ausgewählten Sprache oder null
     */
    getLanguage(languageIso?: string): CoreFrameDTOs.ILanguageDTO {
        let value = languageIso || this.language.value();

        for (let i in this.app.configuration.languages) {
            let language = this.app.configuration.languages[i];

            if (language.iso6391 === value || language.cultureInfoName === value) {
                return language;
            }
        }

        return null;
    }

    /**
     * Retrieves a resource based on the given ergName.
     * If the resource is not found, the ergName itself will be returned.
     *
     * @param {string} ergName - The name of the resource to retrieve.
     * @return {string} - The retrieved resource or the ergName if not found.
     */
    get(ergName: string): string {
        let resources = this.resources();
        let resource: string;

        if (ergName != null) {
            resource = resources[ergName];
            if (!Utilities.isNullOrUndefinedOrBlank(resource)) {
                return resource;
            }

            //Check ErgName Upper
            const ergNameUpper = ergName.toUpperCase();
            resource = resources[ergNameUpper];
            if (!Utilities.isNullOrUndefinedOrBlank(resource)) {
                return resource;
            }
        }
        return ergName;
    }

    /**
     * Load a resource from the this.
     *
     * @param {string} ergName - The name of the resource.
     * @param {boolean} [tryToLoad] - Flag indicating whether to try loading the resource if not found. Default value is false.
     * @return {JQueryPromise<string> | string} - The loaded resource or ergName if not found.
     */
    loadResource(ergName: string, tryToLoad?: boolean): JQueryPromise<string> | string {
        let resources = this.resources();
        let resource = null;

        if (ergName) {
            resource = resources[ergName];
            if (!Utilities.isNullOrUndefinedOrBlank(resource)) {
                return resource;
            } else {
                let upperErgName = ergName.toUpperCase();
                resource = resources[upperErgName];
                if (!Utilities.isNullOrUndefinedOrBlank(resource)) {
                    return resource
                } else if (tryToLoad === true) {
                    return this.requestResourceItem(ergName)
                }
            }

            if (resource == null) {
                resource = ergName;
            }
        }

        return resource;
    }

    /**
     * Requests a resource item from the server.
     *
     * @param {string} ergName - The name of the resource to request.
     * @return {JQueryPromise<string>} - A promise that resolves to the requested resource.
     */
    requestResourceItem(ergName: string): JQueryPromise<string> {
        let language = this.language.value();
        return this.proxy.getResourceItem({
            lang: language,
            resourceName: ergName
        }).then((data) => {
            const resources = this.resources();
            resources[ergName] = data;
            return data;
        }).promise();
    }

    setLanguage<T = true>(callback?: () => T): JQuery.Promise<T> {
        return this.proxy.changeLanguage({
            lang: this.language.value()
        }).then(callback).promise();
    }

    loadLanguageArtfacts(language: string): JQueryPromise<any> {
        let reload = false;
        if (!language) {
            language = this.language.value();
            reload = true;
        }

        let lang = language.toLowerCase();
        if (!(lang in radMessages))
            lang = "en";

        if (lang !== this.currentLoadedLang || reload) {
            this.currentLoadedLang = lang;

            // Die Cultures sind alle bereits geladen worden
            // Die Messages sind auch im Kendo.ui Bundle und können über radMessages aufgerufen werden.
            radMessages[lang]();
            kendo.culture(lang);
            this.overrideCultureInformation();

            return this.proxy.getAllResourceItems({
                lang: language
            }).then((data) => {
                this.resources(data);
            }).promise();
        } else {
            return Utilities.resolveImmediately();
        }
    }

    /**
     * Überschreibt die Standard-Culture-Informationen von Kendo mit denen aus den Metadaten der Sprache
     */
    private overrideCultureInformation() {
        const languages = this.app.configuration.languages;
        const kendoCultures = kendo.cultures;

        languages.forEach((language: CoreFrameDTOs.ILanguageDTO) => {
            const currencyPatternInfo: Interfaces.ICurrencyPatternInfo =
                CurrencyHelper.getCurrencyPatternInfo(language.iso6391);
            const shortName = language.iso6391.toLowerCase();
            const currentCulture = kendoCultures[shortName];

            if (currentCulture === null || currentCulture === undefined)
                return;

            const formats = [
                currentCulture.numberFormat, currentCulture.numberFormat.currency,
                currentCulture.numberFormat.percent
            ];

            // Die Dezimalstellen werden in den Controls jeweils aus anderen Quellen bezogen
            // z.B. die jeweilige Währung, das Format soll bis auf die Dezimalstellen aber für alle Zahlen
            // und Währungen gleich sein
            formats.forEach((f) => {
                f[','] = currencyPatternInfo.groupingSeparator;
                f['.'] = currencyPatternInfo.decimalSeparator;
            });
        });
    }

    getLanguagesForLogin(): any[] {
        return _.filter(this.language.data(), language => language.isLoginEnabled);
    }

    init(usableLanguageIso6391: string): JQueryPromise<any> {
        const languageSelectionActive = this.app.isSystemParamSetAndTrue('Gui.ShowChangeLanguage');

        // Aktualisiert die Liste verfügbarer Sprachen anhand Informationen aus der RADApplication
        // Muss vor dem ersten Aufruf von this.getLanguagesForLogin() ausgeführt werden!
        ko.computed(() => {
            let data = [];

            for (let i in this.app.configuration.languages) {
                let language = this.app.configuration.languages[i];
                (language as any).fullName = LocalizationHelper.getLanguageFullName(language.cultureInfoName);
                language.iconUrl = ImageHandler.convertSource(language.iconUrl);

                data.push(language);
            }

            this.language.data(data);
        });

        // Die Liste der verfügbaren und die aktuell ausgewählte Sprache
        if (!this.language.value()) {
            if (languageSelectionActive)
                this.language.value(this.app.configuration.defaultLanguage.iso6391);
            else
                this.language.value(this.isLanguageUsable(usableLanguageIso6391) ? usableLanguageIso6391 : 'EN');
        } else {
            // Check if the selected language is still available. If not, the default language will be setted.
            const currentLanguage = this.language.value();
            const found = this.isLanguageUsable(currentLanguage);
            if (!found)
                this.language.value(this.app.configuration.defaultLanguage.iso6391);
        }

        let dfd = this.loadLanguageArtfacts(this.language.value());

        // Lädt Ressource-Elemente und Kendo-Sprachdateien sobald die ausgewählte Sprache gewechselt wird
        ko.computed(() => {
            let language = this.language.value();

            // Accessibility: Aktuelle Sprache setzen
            $('html').attr('lang', language);

            if (language != null) {
                this.loadLanguageArtfacts(language);
            }
        });

        return dfd;
    }

    private isLanguageUsable(language: string): boolean {
        const availableLanguages = this.getLanguagesForLogin().map(lang => lang.iso6391.toUpperCase());
        return availableLanguages.indexOf(language) !== -1;
    }

    getTranslationInMLString(mlstring: string, language: string): string {
        if (mlstring.indexOf(this.MLSTRING_SEPARATOR) >= -1) {
            let languages = mlstring.split(this.MLSTRING_SEPARATOR);
            let translation = mlstring;
            languages.forEach((l) => {
                if (l.indexOf(language + ':') == 0)
                    translation = l.substring(language.length + 1);
            });

            return translation;
        } else {
            return mlstring;
        }
    }

    /**
     * Converts a dictionary of translation to a MLString
     * @param translations
     */
    getMLStringFromTranslations(translations: Record<string, string>): string {
        let mappings: string[] = [];
        for (let lang in translations) {
            mappings.push(`${lang}:${translations[lang]}`);
        }

        return this.MLSTRING_SEPARATOR + mappings.join(this.MLSTRING_SEPARATOR);
    }

}